import { observer } from 'mobx-react';
import useAsyncEffect from 'use-async-effect';
import { SpcSteps } from './SpcModels';
import { useParams } from 'react-router-dom';
import SpcViewModel from './SpcViewModel';
import { Appl } from '../../../../core/Appl';
import { Button } from '../../../../core/components/Index';

const AboutEstimate = observer(() => {
  const vm = Appl.Services.get<SpcViewModel>('SpcViewModel');
  let { memberId } = useParams();
  useAsyncEffect(async () => {
    await vm.loadStepAsync(SpcSteps.AboutEstimate, memberId!);
  }, []);

  const type_chosen = vm.model?.spcTypeId;

  //Returning MLOA Path
  if (type_chosen === 1) 
  {
    return (
      <>
        <div className="max-w-6sxl mx-auto space-y-6">
          <h1 className="text-[32px] tracking-[-1%] text-[#363636] font-semibold mb-4">About This Estimate — {vm.spcLovModel.serviceTypeListItems?.find(o=>o.id===vm.model?.spcTypeId)?.name}</h1>
          <div className="w-full max-w-[815px] mb-[24px] leading-[175%] tracking-[-1.5%] text-[15px] font-normal text-[#000000]">
            <p className="mb-[20px]">
              You’ve chosen to estimate the cost of purchasing service credit for a period of unpaid medical leave. 
              This option applies if you were out of SCERS-covered employment due to your own illness and later returned 
              to active service.
            </p>

            <p className="font-bold mb-[20px]">
              What you'll need to provide for each period of leave:
            </p>

            <ul className="mb-[20px] space-y-[2px] ml-[10px] tracking-[1.5%]">
                <li className="flex items-start gap-2">
                  <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />
                  <span className="">
                    Your membership tier at the time of the leave
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />
                  <span className="">
                    The start and end dates of your leave
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />
                  <span className="">
                    The hours of leave (the system will auto-calculate based on your dates; you can adjust to reflect if your leave balances were integrated with State Disability Insurance (SDI) or partial pay)
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />
                  <span className="">
                    Your pay rate and frequency (hourly, per pay period, monthly, or annually)
                  </span>
                </li>
              </ul>

            <p className="font-bold mb-[20px]">
              Important to know:
            </p>

            <ul className="mb-[20px] space-y-[2px] ml-[10px] tracking-[1.5%]">
                <li className="flex items-start gap-2">
                  <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />
                  <span className="">
                    You may enter up to five separate periods of medical leave. You’ll need to provide the same information for each leave period you enter.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />
                  <span className="">
                    Leave periods cannot overlap.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />
                  <span className="">
                    To be eligible, you must have returned to active SCERS-covered employment after each leave.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />
                  <span className="">
                    The maximum service credit purchase for any continuous period of medical leave is one (1) year, which for calculation purposes equals 2,080 hours.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />
                  <span className="">
                    Payment requires repayment of missed contributions plus accrued interest.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />
                  <span className="">
                    This calculation is a preliminary estimate only. The official cost may differ once SCERS reviews 
                    your account and payroll records.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />
                  <span className="">
                    This estimate is based on the full member contribution rate and may not reflect phase-ins, pick-ups, or alternate rates that were less than the full member contribution rate for certain bargaining units in specific years.
                  </span>
                </li>
              </ul>
            <p className="mb-[20px]">
              When you’re ready, continue to enter your information.
            </p>
          </div>
          <div className="flex !mt-[60px] flex-wrap gap-2 sm:flex-nowrap">
            <Button title="Continue" className='border border-[#345719]' rounded
              onClick={async () => await vm.onAboutEstimateStepChangeAsync()}
            />
          </div>
        </div>
      </>
    );
  }

  //Returning Temporary/On-Call Employment Path
  if (type_chosen === 2) 
  {
    return (
      <>
        <div className="max-w-6sxl mx-auto space-y-6">
          <h1 className="text-[32px] tracking-[-1%] text-[#363636] font-semibold mb-4">About This Estimate — {vm.spcLovModel.serviceTypeListItems?.find(o=>o.id===vm.model?.spcTypeId)?.name}</h1>
          <div className="w-full max-w-[815px] mb-[24px] leading-[175%] tracking-[-1.5%] text-[15px] text-[#000000]">
            <p className="mb-[24px] tracking-[-1.5%]">
              You’ve chosen to estimate the cost of purchasing service credit for employment with a SCERS-participating employer that occurred before you became a SCERS member. This option applies to temporary, seasonal, or on-call positions that did not qualify for membership at the time.
            </p>

            <p className="font-bold mb-[24px]">
              What you'll need to provide:
            </p>

            <ul className="mb-[24px] space-y-[2px] ml-[10px] tracking-[1.5%]">
                <li className="flex items-start gap-2">
                  <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />
                  <span className="">
                    The date you entered SCERS membership
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />
                  <span className="">
                    Your membership tier at the time you joined SCERS
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />
                  <span className="">
                    The start and end dates of the employment
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />
                  <span className="">
                    Your pay rate and frequency including any retirement applicable special pays (hourly, per pay period, monthly, or annually)
                  </span>
                </li>
              </ul>

            <p className="font-bold mb-[24px]">
              Important to know:
            </p>

            <ul className="mb-[24px] space-y-[2px] ml-[10px] tracking-[1.5%]">
                <li className="flex items-start gap-2">
                  <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />
                  <span className="">
                    Service performed as a contractor or through a temporary staffing agency is not eligible.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />
                  <span className="">
                    The cost will include contributions that would have been paid during the period, plus accrued interest.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />
                  <span className="">
                    This calculation is a preliminary estimate only. The official cost may differ once SCERS reviews your account and payroll records.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />
                  <span className="">
                    This estimate is based on the full member contribution rate and may not reflect phase-ins, pick-ups, or alternate rates that were less than the full member contribution rate for certain bargaining units in specific years.
                  </span>
                </li>
              </ul>
            <p className="mb-[24px]">
              When you’re ready, continue to enter your information.
            </p>
          </div>
          <div className="flex !mt-[60px] flex-wrap gap-2 sm:flex-nowrap">
            <Button title="Continue" className='border border-[#345719]' rounded
              onClick={async () => await vm.onAboutEstimateStepChangeAsync()}
            />
          </div>
        </div>
      </>
    );
  }

  //Returning Redeposit of Previously Withdrawn Contributions Path
  if (type_chosen === 3) 
  {
    return (
      <>
        <div className="max-w-6sxl mx-auto space-y-6">
          <h1 className="w-full max-w-[800px] text-[32px] tracking-[-1%] text-[#363636] font-semibold mb-4">About This Estimate — {vm.spcLovModel.serviceTypeListItems?.find(o=>o.id===vm.model?.spcTypeId)?.name}</h1>
          <div className="w-full max-w-[815px] mb-[24px] leading-[175%] tracking-[-1.5%] text-[15px] font-normal text-[#000000]">
            <p className="mb-[24px]">
              You’ve chosen to estimate the cost of redepositing contributions you previously withdrew from SCERS after ending membership. By redepositing, you can restore the corresponding service credit and potentially retain your original membership tier.
            </p>

            <p className="font-bold mb-[24px]">
              What you'll need to provide:
            </p>

            <ul className="mb-[24px] space-y-[2px] ml-[10px] tracking-[1.5%]">
                <li className="flex items-start gap-2">
                  <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />
                  <span className="">
                    The date you withdrew your contributions
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />
                  <span className="">
                    The amount of contributions you withdrew including interest.
                  </span>
                </li>
              </ul>

            <p className="font-bold mb-[24px]">
              Important to know:
            </p>

            <ul className="mb-[24px] space-y-[2px] ml-[10px] tracking-[1.5%]">
                <li className="flex items-start gap-2">
                  <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />
                  <span className="">
                    A redeposit requires paying back the full amount of withdrawn contributions plus interest from the date of withdrawal to the present. Partial redeposits are not allowed.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />
                  <span className="">
                    This calculation is a preliminary estimate only. The official cost may differ once SCERS reviews 
                    your account and records.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />
                  <span className="">
                    This estimate is based on the full member contribution rate and may not reflect phase-ins, pick-ups, or alternate rates that were less than the full member contribution rate for certain bargaining units in specific years.
                  </span>
                </li>
              </ul>
            <p className="mb-[24px]">
              When you’re ready, continue to enter your information.
            </p>
          </div>
          <div className="flex !mt-[60px] flex-wrap gap-2 sm:flex-nowrap">
            <Button title="Continue" className='border border-[#345719]' rounded
              onClick={async () => await vm.onAboutEstimateStepChangeAsync()}
            />
          </div>
        </div>
      </>
    );
  }

  //Returning Public Service Path
  if (type_chosen === 4) 
  {
    return (
      <>
        <div className="max-w-6sxl mx-auto">
          <h1 className="text-[32px] tracking-[-1%] text-[#363636] font-semibold mb-[32px]">About This Estimate — {vm.spcLovModel.serviceTypeListItems?.find(o=>o.id===vm.model?.spcTypeId)?.name}</h1>

          <div className="w-full max-w-[820px] mb-[24px] leading-[175%] tracking-[-1.5%] text-[15px] font-normal text-[#000000]">
            <p className="mb-[20px]">
              You’ve chosen to estimate the cost of purchasing service credit for prior public employment outside of SCERS. Eligible employment includes work with the federal government, the State of California, or other municipalities and districts within Sacramento County.
            </p>

            <p className="font-bold mb-[20px]">
              What you'll need to provide:
            </p>

            <ul className="mb-[20px] space-y-[2px] ml-[10px] tracking-[1.5%]">
                <li className="flex items-start gap-2">
                  <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />
                  <span className="">
                    The date you entered SCERS membership
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />
                  <span className="">
                    Your membership tier at the time you joined SCERS
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />
                  <span className="">
                    The start and end dates of the public service
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />
                  <span className="">
                    Your pay rate and frequency, including any retirement applicable special pays (hourly, per pay period, monthly, or annually)
                  </span>
                </li>
              </ul>

            <p className="font-bold mb-[20px]">
              Important to know:
            </p>

            <ul className="mb-[24px] space-y-[2px] ml-[10px] tracking-[1.5%]">
                <li className="flex items-start gap-2">
                  <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />
                  <span className="">
                    You may purchase up to four (4) years of public service credit.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />
                  <span className="">
                    The service must not be covered by another retirement system, unless the retirement contributions can be redeposited with full reciprocal rights.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />
                  <span className="">
                    If you later become eligible for a pension from the public entity, your SCERS service credit and contributions for that period may be adjusted.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />
                  <span className="">
                    This calculation is a preliminary estimate only. The official cost may differ once SCERS reviews your account and payroll records.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />
                  <span className="">
                    This estimate is based on the full member contribution rate and may not reflect phase-ins, pick-ups, or alternate rates that were less than the full member contribution rate for certain bargaining units in specific years.
                  </span>
                </li>
              </ul>
          </div>
          <div className="flex !mt-[55px] flex-wrap gap-2 sm:flex-nowrap">
            <Button title="Continue" className='border border-[#345719]' rounded
              onClick={async () => await vm.onAboutEstimateStepChangeAsync()}
            />
          </div>
        </div>
      </>
    );
  }

  return null;
});

export default AboutEstimate;
